// Skeletal animation of MDM meshes with MDX bones: port of R_CalcBones,
// R_CalcBone / R_CalcBoneLerp (tr_animation_mdm.c, with YD_INGLES as built),
// R_MDM_GetBoneTag and the vertex skinning of RB_MDM_SurfaceAnim.
//
// Legs and torso run their own frames: bones with a torsoWeight blend towards
// the torso frame, then the whole torso (weighted per bone) is rotated by the
// torso axis about the torsoParent bone.
//
// Matrices follow the C code: a bone matrix is three rows (AnglesToAxis), and
// a point is transformed as out[i] = dot(in, matrix[i]) + translation[i].

import type { MdmModel, MdmSurface, MdmTag } from './mdm';
import type { MdxModel } from './mdx';
import type { Vec3 } from '../../shared/math';

export type Axis = [Vec3, Vec3, Vec3];

export interface Orientation {
  origin: Vec3;
  axis: Axis;
}

export const identityAxis = (): Axis => [
  [1, 0, 0],
  [0, 1, 0],
  [0, 0, 1],
];

/** AnglesToAxis: forward, left, up. */
export function anglesToAxis(angles: Vec3, out: Axis = identityAxis()): Axis {
  const d = Math.PI / 180;
  const sy = Math.sin(angles[1] * d);
  const cy = Math.cos(angles[1] * d);
  const sp = Math.sin(angles[0] * d);
  const cp = Math.cos(angles[0] * d);
  const sr = Math.sin(angles[2] * d);
  const cr = Math.cos(angles[2] * d);
  out[0][0] = cp * cy;
  out[0][1] = cp * sy;
  out[0][2] = -sp;
  // axis[1] = -right
  out[1][0] = -(-1 * sr * sp * cy + -1 * cr * -sy);
  out[1][1] = -(-1 * sr * sp * sy + -1 * cr * cy);
  out[1][2] = -(-1 * sr * cp);
  out[2][0] = cr * sp * cy + -sr * -sy;
  out[2][1] = cr * sp * sy + -sr * cy;
  out[2][2] = cr * cp;
  return out;
}

/** MatrixMultiply: out = a * b (3x3, rows). */
export function multiplyAxis(a: Axis, b: Axis): Axis {
  const out = identityAxis();
  for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) out[i][j] = a[i][0] * b[0][j] + a[i][1] * b[1][j] + a[i][2] * b[2][j];
  return out;
}

/**
 * CG_PositionEntityOnTag: a child entity on a tag of its parent (tag in the
 * parent's model space). With `rotation` it is CG_PositionRotatedEntityOnTag.
 */
export function placeOnTag(parent: Orientation, tag: Orientation, rotation?: Axis): Orientation {
  const origin: Vec3 = [parent.origin[0], parent.origin[1], parent.origin[2]];
  for (let i = 0; i < 3; i++) for (let k = 0; k < 3; k++) origin[k] += tag.origin[i] * parent.axis[i][k];
  const axis = rotation ? multiplyAxis(multiplyAxis(rotation, tag.axis), parent.axis) : multiplyAxis(tag.axis, parent.axis);
  return { origin, axis };
}

// --- tr.sinTable based trig of the "ingles" code ---

const FUNCTABLE_SIZE = 4096;
const SIN_TABLE = new Float32Array(FUNCTABLE_SIZE);
for (let i = 0; i < FUNCTABLE_SIZE; i++) SIN_TABLE[i] = Math.sin((i * 360 * Math.PI) / FUNCTABLE_SIZE / 180);
const sinT = (i: number) => SIN_TABLE[(i & 65535) >> 4];
const cosT = (i: number) => SIN_TABLE[(((i & 65535) >> 4) + FUNCTABLE_SIZE / 4) & (FUNCTABLE_SIZE - 1)];

function ingleVector(pitch: number, yaw: number, out: Vec3): Vec3 {
  const sy = sinT(yaw);
  const cy = cosT(yaw);
  const sp = sinT(pitch);
  const cp = cosT(pitch);
  out[0] = cp * cy;
  out[1] = cp * sy;
  out[2] = -sp;
  return out;
}

function inglesToAxis(ingles: Vec3, axis: Axis): void {
  const sy = sinT(ingles[1]);
  const cy = cosT(ingles[1]);
  const sp = sinT(ingles[0]);
  const cp = cosT(ingles[0]);
  const sr = sinT(ingles[2]);
  const cr = cosT(ingles[2]);
  axis[0][0] = cp * cy;
  axis[0][1] = cp * sy;
  axis[0][2] = -sp;
  axis[1][0] = sr * sp * cy + cr * -sy;
  axis[1][1] = sr * sp * sy + cr * cy;
  axis[1][2] = sr * cp;
  axis[2][0] = cr * sp * cy + -sr * -sy;
  axis[2][1] = cr * sp * sy + -sr * cy;
  axis[2][2] = cr * cp;
}

/** SLerp_Normal: linear blend, then VectorNormalizeFast. */
function slerpNormal(from: Vec3, to: Vec3, t: number, out: Vec3): Vec3 {
  const ft = 1 - t;
  const x = from[0] * ft + to[0] * t;
  const y = from[1] * ft + to[1] * t;
  const z = from[2] * ft + to[2] * t;
  const len = Math.hypot(x, y, z) || 1;
  out[0] = x / len;
  out[1] = y / len;
  out[2] = z / len;
  return out;
}

const short2angle = (x: number) => x * (360 / 65536);
const angleNormalize180 = (a: number) => {
  // AngleNormalize360 / 180 through the 16 bit angle, like q_math.c
  const n = (360 / 65536) * (Math.trunc(a * (65536 / 360)) & 65535);
  return n > 180 ? n - 360 : n;
};
/** ( a - b ) & 65535 as a signed short */
const wrap16 = (x: number) => {
  const v = x & 65535;
  return v > 32767 ? v - 65536 : v;
};

// --- bones ---

export interface BoneFrame {
  matrix: Axis;
  translation: Vec3;
}

/** What R_CalcBones reads from the refEntity. */
export interface SkeletonPose {
  frameModel: MdxModel;
  frame: number;
  oldFrameModel: MdxModel;
  oldFrame: number;
  backlerp: number;
  torsoFrameModel: MdxModel;
  torsoFrame: number;
  oldTorsoFrameModel: MdxModel;
  oldTorsoFrame: number;
  torsoBacklerp: number;
  /** Torso rotation relative to the legs (refent.torsoAxis). */
  torsoAxis: Axis;
}

const newBone = (): BoneFrame => ({ matrix: identityAxis(), translation: [0, 0, 0] });

/** Bone list of one skeleton, reused between frames. */
export class Skeleton {
  readonly bones: BoneFrame[];

  constructor(readonly numBones: number) {
    this.bones = Array.from({ length: numBones }, newBone);
  }

  /** R_CalcBones for every bone of the skeleton. */
  calc(pose: SkeletonPose): void {
    const mdx = pose.frameModel;
    const n = Math.min(this.numBones, mdx.numBones);
    const clampFrame = (m: MdxModel, f: number) => Math.min(Math.max(f, 0), m.numFrames - 1);
    const frame = clampFrame(mdx, pose.frame);
    const oldFrame = clampFrame(pose.oldFrameModel, pose.oldFrame);
    const torsoFrame = clampFrame(pose.torsoFrameModel, pose.torsoFrame);
    const oldTorsoFrame = clampFrame(pose.oldTorsoFrameModel, pose.oldTorsoFrame);

    let backlerp: number;
    let frontlerp: number;
    if (oldFrame === frame && pose.oldFrameModel === pose.frameModel) {
      backlerp = 0;
      frontlerp = 1;
    } else {
      backlerp = pose.backlerp;
      frontlerp = 1 - backlerp;
    }
    let torsoBacklerp: number;
    let torsoFrontlerp: number;
    // (sic) compares the old torso frame model with the old legs frame model
    if (oldTorsoFrame === torsoFrame && pose.oldTorsoFrameModel === pose.oldFrameModel) {
      torsoBacklerp = 0;
      torsoFrontlerp = 1;
    } else {
      torsoBacklerp = pose.torsoBacklerp;
      torsoFrontlerp = 1 - torsoBacklerp;
    }

    const legs = mdx.boneFrames;
    const legsBase = frame * mdx.numBones * 6;
    const torso = pose.torsoFrameModel.boneFrames;
    const torsoBase = torsoFrame * pose.torsoFrameModel.numBones * 6;
    const oldLegs = pose.oldFrameModel.boneFrames;
    const oldLegsBase = oldFrame * pose.oldFrameModel.numBones * 6;
    const oldTorso = pose.oldTorsoFrameModel.boneFrames;
    const oldTorsoBase = oldTorsoFrame * pose.oldTorsoFrameModel.numBones * 6;
    const parentOffset = (m: MdxModel, f: number, k: number) => m.parentOffsets[f * 3 + k];

    const done = new Uint8Array(n);
    const torsoParentOffset: Vec3 = [0, 0, 0];
    const angles: Vec3 = [0, 0, 0];
    const tangles: Vec3 = [0, 0, 0];
    const vec: Vec3 = [0, 0, 0];
    const v2: Vec3 = [0, 0, 0];
    const dir: Vec3 = [0, 0, 0];
    const lerp = backlerp !== 0 || torsoBacklerp !== 0;

    const calcBone = (b: number): void => {
      if (done[b]) return;
      const info = mdx.bones[b];
      if (info.parent >= 0) calcBone(info.parent);
      const bone = this.bones[b];
      const isTorso = info.torsoWeight !== 0;
      const fullTorso = info.torsoWeight === 1;
      const l = legsBase + b * 6;
      const t = torsoBase + b * 6;
      const ol = oldLegsBase + b * 6;
      const ot = oldTorsoBase + b * 6;

      if (!lerp) {
        // R_CalcBone
        if (fullTorso) {
          for (let j = 0; j < 3; j++) angles[j] = short2angle(torso[t + j]);
        } else {
          for (let j = 0; j < 3; j++) angles[j] = short2angle(legs[l + j]);
          if (isTorso) {
            for (let j = 0; j < 3; j++) {
              tangles[j] = short2angle(torso[t + j]);
              let diff = tangles[j] - angles[j];
              if (Math.abs(diff) > 180) diff = angleNormalize180(diff);
              angles[j] = angles[j] + info.torsoWeight * diff;
            }
          }
        }
        anglesToAxis(angles, bone.matrix);
        if (info.parent >= 0) {
          const parent = this.bones[info.parent];
          if (fullTorso) ingleVector(torso[t + 4], torso[t + 5], vec);
          else {
            ingleVector(legs[l + 4], legs[l + 5], vec);
            if (isTorso) {
              ingleVector(torso[t + 4], torso[t + 5], v2);
              slerpNormal(vec, v2, info.torsoWeight, vec);
            }
          }
          for (let k = 0; k < 3; k++) bone.translation[k] = parent.translation[k] + info.parentDist * vec[k];
        } else {
          for (let k = 0; k < 3; k++) bone.translation[k] = parentOffset(mdx, frame, k);
        }
      } else {
        // R_CalcBoneLerp
        const ingles = angles;
        if (fullTorso) {
          for (let j = 0; j < 3; j++) ingles[j] = Math.trunc(torso[t + j] - torsoBacklerp * wrap16(torso[t + j] - oldTorso[ot + j]));
        } else {
          for (let j = 0; j < 3; j++) ingles[j] = Math.trunc(legs[l + j] - backlerp * wrap16(legs[l + j] - oldLegs[ol + j]));
          if (isTorso) {
            for (let j = 0; j < 3; j++) {
              let tj = Math.trunc(torso[t + j] - torsoBacklerp * wrap16(torso[t + j] - oldTorso[ot + j]));
              tj = wrap16(tj - ingles[j]);
              ingles[j] = Math.trunc(ingles[j] + info.torsoWeight * tj);
            }
          }
        }
        inglesToAxis(ingles, bone.matrix);
        if (info.parent >= 0) {
          const parent = this.bones[info.parent];
          if (fullTorso) {
            ingleVector(torso[t + 4], torso[t + 5], v2);
            ingleVector(oldTorso[ot + 4], oldTorso[ot + 5], vec);
            slerpNormal(vec, v2, torsoFrontlerp, dir);
          } else {
            ingleVector(legs[l + 4], legs[l + 5], v2);
            ingleVector(oldLegs[ol + 4], oldLegs[ol + 5], vec);
            slerpNormal(vec, v2, frontlerp, dir);
          }
          if (!fullTorso && isTorso) {
            // (sic) the YD_INGLES path reads the new torso frame for both ends
            ingleVector(torso[t + 4], torso[t + 5], v2);
            ingleVector(torso[t + 4], torso[t + 5], vec);
            slerpNormal(vec, v2, torsoFrontlerp, v2);
            slerpNormal(dir, v2, info.torsoWeight, dir);
          }
          for (let k = 0; k < 3; k++) bone.translation[k] = parent.translation[k] + info.parentDist * dir[k];
        } else {
          for (let k = 0; k < 3; k++) bone.translation[k] = frontlerp * parentOffset(mdx, frame, k) + backlerp * parentOffset(pose.oldFrameModel, oldFrame, k);
        }
      }
      if (b === mdx.torsoParent) for (let k = 0; k < 3; k++) torsoParentOffset[k] = bone.translation[k];
      done[b] = 1;
    };
    for (let b = 0; b < n; b++) calcBone(b);

    // rotate the torso bones about the torsoParent
    const ta = pose.torsoAxis;
    const torsoAxis: Axis = [
      [ta[0][0], ta[1][0], ta[2][0]],
      [ta[0][1], ta[1][1], ta[2][1]],
      [ta[0][2], ta[1][2], ta[2][2]],
    ];
    const m2 = identityAxis();
    let m2Weight = 0;
    const tr: Vec3 = [0, 0, 0];
    const rot = identityAxis();
    for (let b = 0; b < n; b++) {
      const w = mdx.bones[b].torsoWeight;
      if (w <= 0) continue;
      const bone = this.bones[b];
      if (m2Weight !== w) {
        // Matrix4FromScaledAxisPlusTranslation
        for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) m2[i][j] = w * torsoAxis[i][j] + (i === j ? 1 - w : 0);
        m2Weight = w;
      }
      for (let k = 0; k < 3; k++) tr[k] = bone.translation[k] - torsoParentOffset[k];
      for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) rot[i][j] = m2[i][0] * bone.matrix[0][j] + m2[i][1] * bone.matrix[1][j] + m2[i][2] * bone.matrix[2][j];
      }
      for (let i = 0; i < 3; i++) {
        bone.translation[i] = m2[i][0] * tr[0] + m2[i][1] * tr[1] + m2[i][2] * tr[2] + torsoParentOffset[i];
        for (let j = 0; j < 3; j++) bone.matrix[i][j] = rot[i][j];
      }
    }
  }

  /** R_MDM_GetBoneTag (after calc()): the tag in model space. */
  tag(tag: MdmTag): Orientation {
    const bone = this.bones[tag.boneIndex];
    const m = bone.matrix;
    const o = tag.offset;
    const origin: Vec3 = [0, 0, 0];
    for (let i = 0; i < 3; i++) origin[i] = o[0] * m[i][0] + o[1] * m[i][1] + o[2] * m[i][2] + bone.translation[i];
    const axis = identityAxis();
    for (let j = 0; j < 3; j++) {
      const a = tag.axis[j];
      for (let i = 0; i < 3; i++) axis[j][i] = a[0] * m[i][0] + a[1] * m[i][1] + a[2] * m[i][2];
    }
    return { origin, axis };
  }

  /**
   * RB_MDM_SurfaceAnim: model space positions (sum of the weighted bone
   * transforms) and normals (from the first weight's bone) of a surface.
   */
  skin(surf: MdmSurface, positions: Float32Array, normals: Float32Array): void {
    const bones = this.bones;
    for (let v = 0; v < surf.numVerts; v++) {
      let x = 0;
      let y = 0;
      let z = 0;
      const start = surf.weightStart[v];
      const end = start + surf.weightCount[v];
      for (let w = start; w < end; w++) {
        const bone = bones[surf.weightBone[w]];
        const m = bone.matrix;
        const t = bone.translation;
        const s = surf.weightValue[w];
        const ox = surf.weightOffset[w * 3];
        const oy = surf.weightOffset[w * 3 + 1];
        const oz = surf.weightOffset[w * 3 + 2];
        x += s * (ox * m[0][0] + oy * m[0][1] + oz * m[0][2] + t[0]);
        y += s * (ox * m[1][0] + oy * m[1][1] + oz * m[1][2] + t[1]);
        z += s * (ox * m[2][0] + oy * m[2][1] + oz * m[2][2] + t[2]);
      }
      positions[v * 3] = x;
      positions[v * 3 + 1] = y;
      positions[v * 3 + 2] = z;
      const m = bones[surf.weightBone[start]].matrix;
      const nx = surf.normals[v * 3];
      const ny = surf.normals[v * 3 + 1];
      const nz = surf.normals[v * 3 + 2];
      normals[v * 3] = nx * m[0][0] + ny * m[0][1] + nz * m[0][2];
      normals[v * 3 + 1] = nx * m[1][0] + ny * m[1][1] + nz * m[1][2];
      normals[v * 3 + 2] = nx * m[2][0] + ny * m[2][1] + nz * m[2][2];
    }
  }
}

/** Tag of a skeletal model by name, or null. */
export function findMdmTag(mdm: MdmModel, name: string): MdmTag | null {
  return mdm.tags.find((t) => t.name === name) ?? null;
}
